// Record Receipt drawer: the per-line card, the dry-run preview block and the success body. Presentational; the rules live in receiptModel.mjs.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { IcoLn } from '../../../shell/ActionIcons';
import { Emo } from '../../../shell/EmojiIcons';
import { formatDate, formatQty, formatMoney, formatMoneyPrecise } from '../../../utils/format';
import { selectableStorages, isStorageSelectable, storageOptionLabel } from '../../../utils/storageSelectable';
import { isBirdLine, lineOpen, lineIssue, priceDiff, defaultCost, num } from './receiptModel.mjs';
import EnumSelect from '../../../components/EnumSelect';
import { AmountInput, TextInput } from '../../../components/inputs/Input';
import { useQtySanitizer } from '../../../hooks/useItemQtyDecimals';

const fmtF = n => formatMoney(Math.round(Number(n) || 0));
const fmtP = n => formatMoneyPrecise(Number(n) || 0);   // unit prices keep the rate decimals
const money = v => (v == null ? '—' : formatMoney(v));

export function LineCard({ line, st, setSt, storages, isCredit }) {
  const { t } = useTranslation('receipt'); const sq = useQtySanitizer();   // W13 (GES-388): the received quantity typed at the item's own decimals
  const { qtys, unitCosts, storageIds, closeLines } = st;
  const closed = closeLines.has(line.id), bird = isBirdLine(line), q = qtys[line.id] || '';
  const issue = !closed && q !== '' ? lineIssue(line, q) : null;
  const diff = closed ? null : priceDiff(line, unitCosts, q);
  const live = selectableStorages(storages, storageIds[line.id] || line.storage_id || null)
    .filter(s => { const cat = String(line.category || '').toLowerCase(); return !cat || s.constant_key === cat; });
  const left = lineOpen(line) - (parseFloat(q) || 0);
  const put = (k, v) => setSt(s => ({ ...s, [k]: { ...s[k], [line.id]: v } }));
  const toggleClose = on => setSt(s => { const n = new Set(s.closeLines); if (on) n.add(line.id); else n.delete(line.id); return { ...s, closeLines: n }; });
  const u = line.unit ? ' ' + line.unit : '';
  return (
    <div className={'rcp-line' + (closed ? ' closed' : '')} data-testid={'rcp-line-' + line.line_id}>
      <div className="rcp-name">{line.item_name || '—'}</div>
      <div className="rcp-strip">
        <div><div className="k">{t('strip.ordered')}</div><div className="v">{formatQty(line.qty_ordered)}{u}</div></div>
        <div><div className="k">{t('strip.received')}</div><div className="v">{formatQty(line.qty_received)}{u}</div></div>
        <div><div className="k">{t('strip.open')}</div><div className="v" data-testid={'rcp-open-' + line.line_id}>{formatQty(lineOpen(line))}{u}</div></div>
        <div><div className="k">{t('strip.lineEta')}</div><div className="v">{line.expected_receipt_date ? formatDate(line.expected_receipt_date) : t('none')}</div></div>
      </div>
      <div className="form">
        <div className="field"><label htmlFor={'rcp-q-' + line.id}>{t('line.qty')}</label>
          <TextInput id={'rcp-q-' + line.id} type="number" min="0" step="any" placeholder="0" disabled={closed} value={q} className={issue ? 'bad' : undefined} aria-invalid={!!issue}
            aria-label={t('line.qtyFor', { item: line.item_name })} data-testid={'rcp-qty-' + line.line_id} onChange={e => put('qtys', sq(e.target.value, line.item_id))} /></div>
        <div className="field"><label htmlFor={'rcp-c-' + line.id}>{t('line.unitCost')}</label>
          <AmountInput id={'rcp-c-' + line.id} min="0" step="any" disabled={closed} value={unitCosts[line.id] ?? defaultCost(line)} data-testid={'rcp-cost-' + line.line_id} onChange={e => put('unitCosts', e.target.value)} /></div>
        {bird ? (
          <div className="field"><label>{t('line.band')}</label><div className="rcp-band" title={t('line.bandTitle')}>{line.band_name || '—'}</div></div>
        ) : (
          <div className="field"><label htmlFor={'rcp-s-' + line.id}>{t('line.storage')}</label>
            <EnumSelect bare id={'rcp-s-' + line.id} disabled={closed} value={storageIds[line.id] || ''} testid={'rcp-storage-' + line.line_id} onChange={v => put('storageIds', v)}>
              <option value="">{t('line.noStorage')}</option>
              {live.map(s => <option key={s.id} value={s.id} disabled={!isStorageSelectable(s)}>{storageOptionLabel(s)}</option>)}
            </EnumSelect></div>
        )}
        <div className="field"><label className="chk" title={num(line.qty_received) === 0 ? t('line.cancelTitleNone') : t('line.cancelTitleSome', { received: formatQty(line.qty_received) })}>
          <input type="checkbox" checked={closed} data-testid={'rcp-cancel-' + line.line_id} onChange={e => toggleClose(e.target.checked)} /> {t('line.cancelRemaining')}</label></div>
      </div>
      {closed && <div className="rcp-note">{t('line.willClose')}</div>}
      {!closed && !issue && (parseFloat(q) || 0) > 0 && left > 0.0001 && <div className="rcp-note" data-testid="rcp-partial">{t('line.partialNote', { left: formatQty(left) + u })}</div>}
      {issue === 'over' && <div className="ferr" role="alert" data-testid="rcp-over"><Emo name="stop" /> {t('err.over', { item: line.item_name, qty: formatQty(q), open: formatQty(lineOpen(line)), ordered: formatQty(line.qty_ordered), received: formatQty(line.qty_received) })}</div>}
      {issue === 'negative' && <div className="ferr" role="alert"><Emo name="stop" /> {t('err.negative', { item: line.item_name })}</div>}
      {diff && (
        <div className="warnbox" role="status" data-testid="rcp-pricediff"><Emo name="warn" /> <div>
          {t('line.priceDiff', { receipt: fmtP(diff.receiptPrice), order: fmtP(diff.orderPrice), dir: t(diff.up ? 'line.up' : 'line.down'), amount: fmtF(Math.abs(diff.delta)) })}{' '}
          {isCredit ? t('line.apChange', { sign: diff.up ? '+' : '−', amount: fmtF(Math.abs(diff.delta)) }) : t('line.needFunding')}</div></div>)}
    </div>
  );
}

function TieOut({ tie }) {
  const { t } = useTranslation('receipt');
  if (!tie || (!tie.block && !tie.warn)) return null;
  const node = [tie.node && tie.node.item, tie.node && tie.node.name].filter(Boolean).join(' · ') || t('tie.thisNode');
  return tie.block
    ? <div className="ferr" role="alert" data-testid="rcp-tie-block"><Emo name="stop" /> {t('tie.block', { gap: fmtF(Math.abs(tie.divergence)), fin: fmtF(tie.finance_delta), phys: fmtF(tie.physical_delta), node })}</div>
    : <div className="warnbox" role="status"><Emo name="warn" /> <div>{t('tie.warn', { node, severity: tie.severity_after, gap: fmtF(tie.gap_after) })}</div></div>;
}

export function PreviewBlock({ rows }) {
  const { t } = useTranslation('receipt');
  return (
    <div className="rcp-prev" data-testid="rcp-preview">
      <div className="sec-lab" style={{ margin: 0 }}>{t('preview.title')}</div>
      {rows.map(({ line, qty, projection: p, close: c, tie_out: tie }) => (
        <div key={line.id} style={{ display: 'grid', gap: 4 }}>
          {c ? (
            <>
              <b>{t(c.disposition === 'close_short' ? 'preview.closeShort' : 'preview.closeNone', { item: line.item_name })}</b>
              <div>{t('preview.forfeited', { qty: formatQty(c.qty_forfeited || 0), value: fmtF(c.forfeit_value), status: c.new_receipt_status })}</div>
              {(c.projected_legs || []).map((lg, i) => <div key={i}>{t('preview.leg', { subtype: lg.subtype, amount: fmtF(lg.amount), effect: lg.effect })}</div>)}
              {c.choice_required && <div className="ferr" role="alert"><Emo name="warn" /> {t('preview.choice')}</div>}
              <div className="rcp-note">{t('preview.closeNote')}</div>
            </>
          ) : !p ? <div className="rcp-note">{t('preview.noProjection', { item: line.item_name })}</div> : (
            <>
              <b>{t('preview.units', { item: line.item_name, qty: formatQty(qty) })}</b>
              <div className="grid">
                <span>{t('preview.baseCost')}</span><span className="r">{fmtP(p.base_unit_cost)}</span>
                <span>{t('preview.lcShare')}</span><span className="r">{money(p.lc_share)}</span>
                <b>{t('preview.exitCost')}</b><b className="r">{fmtP(p.exit_unit_cost)}</b>
                <span>{t('preview.storageNow')}</span><span className="r">{formatQty(p.before?.qty || 0)} @ {fmtP(p.current_avg)} = {money(p.before?.value)}</span>
                <b>{t('preview.storageAfter')}</b><b className="r">{formatQty(p.after?.qty || 0)} @ {fmtP(p.after?.avg)} = {money(p.after?.value)}</b>
                <span>{t('preview.holding')}</span><span className="r">{money(p.remaining_holding)}</span>
              </div>
            </>
          )}
          <TieOut tie={tie} />
        </div>
      ))}
      {rows[0]?.projection?.messages && <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11.5 }}>{rows[0].projection.messages.map((m, i) => <li key={i}>{m}</li>)}</ul>}
    </div>
  );
}

export function DoneBody({ done, more }) {
  const { t } = useTranslation('receipt');
  return (
    <div style={{ display: 'grid', gap: 12 }} data-testid="rcp-done">
      <div className="okbox succ" role="status">
        <div><Emo name="check" /> {t('done.title')}</div>
        {done.received.map(({ line, qty }) => <div key={line.id}>{t('done.line', { qty: formatQty(qty), unit: line.unit || '', item: line.item_name })}</div>)}
        {done.closed.map(({ line, short }) => <div key={line.id}>{t('done.closedLine', { item: line.item_name, kind: t(short ? 'done.closeShort' : 'done.closeCancel') })}</div>)}
        <div className="okmsg">{t('done.saved')}</div>
      </div>
      {done.echoes.length > 0 && (
        <div className="rcp-echo">
          {done.echoes.map((e, i) => <div key={i}>{e.item_name ? <b>{e.item_name}: </b> : null}{t('done.before')}: {formatQty(e.before?.qty || 0)} <IcoLn name="close" /> {money(e.before?.avg)} → {t('done.after')}: {formatQty(e.after?.qty || 0)} <IcoLn name="close" /> {money(e.after?.avg)}</div>)}
          <div className="rcp-note" style={{ fontStyle: 'italic', marginTop: 4 }}>{t('done.echoNote')}</div>
        </div>)}
      {more === false && <div className="hint" data-testid="rcp-nothing-left"><Emo name="check" /> {t('done.nothingLeft')}</div>}
      {more === true && <div className="hint"><Emo name="link" /> {t('done.stillOpen')}</div>}
    </div>
  );
}
